import { isNumber } from '@trmediaab/zebra-utils';
import classnames from 'classnames';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import TextInput from './TextInput';

const NumberInput = ({
  value,
  minValue = 0,
  maxValue = 999,
  step = 1,
  allowEmpty = true,
  emptyValue = -1,
  placeholder = '',
  disabled = false,
  width = '50',
  onClick = noop,
  onChange,
  onBlur = noop,
}) => {
  const isValid = useCallback(
    value => {
      if (value === '') {
        return allowEmpty;
      }
      if (value == null || !isNumber(value)) {
        return false;
      }

      return value >= minValue && value <= maxValue;
    },
    [allowEmpty, maxValue, minValue],
  );

  const startValue = useRef(null);
  const lastEventValue = useRef(value);
  const lastMinValue = useRef(minValue);
  const lastMaxValue = useRef(maxValue);
  const [internalValue, setInternalValue] = useState(
    isValid(value) ? value : '',
  );

  useEffect(() => {
    if (
      String(value) !== String(lastEventValue.current) ||
      minValue !== lastMinValue.current ||
      maxValue !== lastMaxValue.current
    ) {
      lastEventValue.current = value;
      lastMinValue.current = minValue;
      lastMaxValue.current = maxValue;
      setInternalValue(isValid(value) ? value : '');
    }
  }, [internalValue, isValid, value, minValue, maxValue]);

  const changeHandler = useCallback(
    inputValue => {
      let parsedInputValue =
        inputValue === '' ? inputValue : Number.parseInt(inputValue, 10);
      // Allow decrementing number input into empty string
      if (allowEmpty && parsedInputValue <= emptyValue) {
        parsedInputValue = '';
      }
      setInternalValue(parsedInputValue);
      if (isValid(parsedInputValue)) {
        lastEventValue.current = parsedInputValue;
        // Empty string should never be passed to the parent in onChange
        // If the field is left empty, undefined should be sent
        onChange(
          lastEventValue.current !== '' ? lastEventValue.current : undefined,
        );
      }
    },
    [isValid, onChange, allowEmpty, emptyValue],
  );

  const focusHandler = useCallback(() => {
    startValue.current = value;
  }, [value]);

  const blurHandler = useCallback(() => {
    if (isValid(internalValue)) {
      lastEventValue.current = internalValue;
    } else {
      lastEventValue.current = startValue.current;
      // lastEventValue.current may be undefined if the value prop was undefined
      setInternalValue(
        isValid(lastEventValue.current) ? lastEventValue.current : '',
      );
      // Never pass empty string to parent, pass undefined instead
      onChange(
        lastEventValue.current !== '' ? lastEventValue.current : undefined,
      );
    }
    // Never pass empty string to parent, pass undefined instead
    onBlur(lastEventValue.current !== '' ? lastEventValue.current : undefined);
    startValue.current = null;
  }, [internalValue, isValid, onBlur, onChange]);

  return (
    <TextInput
      type="number"
      inputMode="numeric"
      className={classnames(`number-input number-input--${width}`, {
        'number-input--invalid': !isValid(internalValue),
        'number-input--disabled': disabled,
      })}
      min={allowEmpty ? emptyValue : 0}
      value={internalValue}
      placeholder={placeholder}
      disabled={disabled}
      step={step}
      onClick={onClick}
      onChange={changeHandler}
      onFocus={focusHandler}
      onBlur={blurHandler}
    />
  );
};

NumberInput.propTypes = {
  value: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  step: PropTypes.number,
  allowEmpty: PropTypes.bool,
  emptyValue: PropTypes.number,
  disabled: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOf(['50', '80', 'auto']),
  onClick: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
};

export default NumberInput;
